import React, { useEffect, PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/Edit';
import LinearProgress from '@material-ui/core/LinearProgress';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import Alert from '../../../../../components/alert/Alert';
import { dateInTz } from '../../../../../utils/functions/dates';
import translation from '../../../../../translation/translation';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2)
  },
  tableContainer: {
    maxHeight: 350
  },
  loadingWrapper: {
    width: '100%',
    height: 350,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      height: 'auto'
    }
  }
}));

class CustomHref extends PureComponent {
  render() {
    return <a {...this.props}>{this.props.children}</a>;
  }
}

CustomHref.propTypes = {
  children: PropTypes.any
};

function InvoiceQueue(props) {
  const classes = useStyles();

  const columns = ['id', 'description', 'total_formatted', 'created'];

  const {
    cleanProviderViewInvoiceQueueReducer,
    companyId,
    getProviderViewInvoiceQueue,
    invoiceQueue,
    isModeCompany,
    providerId
  } = props;

  useEffect(() => {
    loadInvoiceQueue(providerId);

    return () => {
      cleanProviderViewInvoiceQueueReducer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModeCompany]);

  function loadInvoiceQueue(providerId, next = null) {
    getProviderViewInvoiceQueue(providerId, next, isModeCompany, companyId);
  }

  function handleInfiniteScroll(event) {
    if (!event) console.error('No table scroll event found.');

    if (!event.currentTarget.scrollTop || event.currentTarget.scrollTop === 0) return;

    if (
      event.currentTarget.scrollHeight - event.currentTarget.clientHeight <=
      event.currentTarget.scrollTop
    ) {
      if (invoiceQueue && invoiceQueue.paging && invoiceQueue.paging.next) {
        getProviderViewInvoiceQueue(providerId, invoiceQueue.paging.next);
      }
    }
  }

  return (
    <div className={classNames(classes.root, 'ProviderInvoiceQueue')}>
      {!invoiceQueue.isLoading &&
      invoiceQueue.error &&
      (!invoiceQueue.data || invoiceQueue.data.length <= 0) ? (
        <div className="error-list">
          <Alert
            type="danger"
            style={{ marginTop: 10 }}
            status={translation().commons.alerts.error}
            text={translation().core.list.error_list}
          />
        </div>
      ) : (
        false
      )}
      {invoiceQueue.data && invoiceQueue.data.length > 0 ? (
        <TableContainer onScroll={handleInfiniteScroll} className={classes.tableContainer}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column}>{column}</TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {invoiceQueue.data.map((invoice, index) => (
                <TableRow key={index}>
                  <TableCell>{invoice.id}</TableCell>
                  <TableCell>
                    {invoice.description.length
                      ? invoice.description.split('\n').map((line, key) => {
                          return (
                            <span key={key}>
                              {line}
                              <br />
                            </span>
                          );
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>{invoice.total_formatted ? invoice.total_formatted : '_'}</TableCell>
                  <TableCell>
                    {invoice.created ? dateInTz(invoice.created, 'localized-datetime') : '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      aria-label="update"
                      component={CustomHref}
                      target="_blank"
                      size="small"
                      href={`/${isModeCompany ? 'companies' : 'providers'}/${
                        isModeCompany && companyId ? companyId : providerId
                      }/invoice-queue/${invoice.id}/update`}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : !invoiceQueue.isLoading && !invoiceQueue.error ? (
        <Alert
          type="info"
          style={{ marginTop: 10 }}
          status={translation().commons.alerts.info}
          text={translation().views.provider_view.invoice_queue.empty}
        />
      ) : (
        false
      )}
      {invoiceQueue.isLoading && !invoiceQueue.paging && (
        <div className={classNames(classes.loadingWrapper, 'loader-wpr')}>
          <div style={{ width: '100%' }}>
            <CircularProgress color="primary" />
            <p>{translation().views.provider_view.invoice_queue.loading_text}</p>
          </div>
        </div>
      )}
      {invoiceQueue.isLoading && invoiceQueue.paging && invoiceQueue.paging.next ? (
        <>
          <LinearProgress color="primary" />
          <div className="loader-wpr linear">
            <p>{translation().core.list.load_next}</p>
          </div>
        </>
      ) : (
        <div className="end-of-list-wpr">
          {!invoiceQueue.isLoading &&
            invoiceQueue.paging &&
            !invoiceQueue.paging.next &&
            invoiceQueue.paging.prev && <p>{translation().core.list.load_next_done}</p>}
        </div>
      )}
    </div>
  );
}

InvoiceQueue.propTypes = {
  cleanProviderViewInvoiceQueueReducer: PropTypes.func.isRequired,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  getProviderViewInvoiceQueue: PropTypes.func.isRequired,
  invoiceQueue: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape()),
    error: PropTypes.string,
    isLoading: PropTypes.bool,
    paging: PropTypes.shape({
      next: PropTypes.string,
      prev: PropTypes.string
    })
  }).isRequired,
  isModeCompany: PropTypes.bool,
  providerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default InvoiceQueue;
